import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import './backlinks-panel.css';

// 백링크 패널 (마이그레이션 0023) — "이 페이지를 링크한 페이지" 목록 오버레이.
// PageEditor 를 수정하지 않기 위해 전역 CustomEvent 로 연결한다:
//   PageOptionsMenu "백링크" 클릭 → window 'rt:open-backlinks' ({ detail: { pageId } })
//   → 상시 마운트된(Sidebar) 본 컴포넌트가 수신해 패널을 연다.
// 데이터는 RPC workspace_get_backlinks(p_page) — RPC 미존재 시 0023 적용 안내.

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return '';
  }
}

export default function BacklinksPanel() {
  const navigate = useNavigate();
  const [pageId, setPageId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState(''); // '' | 'migration' | 'error'

  // 열기 이벤트 수신
  useEffect(() => {
    const h = (e) => {
      const id = e && e.detail && e.detail.pageId;
      if (id) setPageId(id);
    };
    window.addEventListener('rt:open-backlinks', h);
    return () => window.removeEventListener('rt:open-backlinks', h);
  }, []);

  const close = useCallback(() => setPageId(null), []);

  // 백링크 로드
  useEffect(() => {
    if (!pageId) return undefined;
    let active = true;
    setLoading(true);
    setRows([]);
    setStatus('');
    (async () => {
      try {
        const { data, error } = await supabase.rpc('workspace_get_backlinks', { p_page: pageId });
        if (!active) return;
        if (error) {
          // RPC 미존재(0023 미적용): PostgREST 함수 못 찾음(PGRST202) / PG 42883
          const missing = error.code === 'PGRST202' || error.code === '42883'
            || /workspace_get_backlinks/i.test(error.message || '');
          setStatus(missing ? 'migration' : 'error');
        } else {
          setRows(data || []);
        }
      } catch (e) {
        if (active) setStatus('error');
      }
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [pageId]);

  // ESC 로 닫기
  useEffect(() => {
    if (!pageId) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pageId, close]);

  if (!pageId) return null;

  return createPortal(
    <div className="bl-overlay" onClick={close}>
      <aside
        className="bl-panel"
        role="dialog"
        aria-label="백링크"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="bl-head">
          <span className="bl-title">🔗 백링크</span>
          <button type="button" className="bl-close" onClick={close} aria-label="백링크 닫기">✕</button>
        </header>
        <div className="bl-sub">
          {loading || status ? '' : `이 페이지를 링크한 페이지 ${rows.length}개`}
        </div>
        <div className="bl-list">
          {loading ? (
            <div className="bl-empty">불러오는 중…</div>
          ) : status === 'migration' ? (
            <div className="bl-empty">
              백링크 기능을 사용하려면<br />마이그레이션 0023 적용이 필요합니다.
            </div>
          ) : status === 'error' ? (
            <div className="bl-empty">백링크를 불러오지 못했습니다.</div>
          ) : rows.length === 0 ? (
            <div className="bl-empty">아직 이 페이지를 링크한 페이지가 없습니다.</div>
          ) : (
            rows.map((r) => (
              <button
                key={r.page_id}
                type="button"
                className="bl-item"
                onClick={() => { navigate(`/page/${r.page_id}`); close(); }}
              >
                <span className="bl-item-icon">{r.icon || '📄'}</span>
                <span className="bl-item-title">{r.title || '제목 없음'}</span>
                <span className="bl-item-time">{formatDate(r.updated_at)}</span>
              </button>
            ))
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
}
